import React, { useEffect, useMemo, useRef } from "react";
import { CircleMarker, GeoJSON, MapContainer, Pane, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { eventColor, timeAgo } from "../utils/nws";
import { BASEMAPS, SATELLITES, hemisphereFor, reportColor } from "./radarConfig";

const escapeHtml = (value = "") => String(value).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));

const MapBridge = ({ mapRef, onViewChange }) => {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      onViewChange({ lat: center.lat, lon: center.lng, zoom: map.getZoom() });
    },
  });
  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
};

const RadarFrames = ({ product, site, frame, opacity, bucket, frameCount }) => {
  const tiles = useMemo(() => {
    if (product.site && !site) return [];
    return Array.from({ length: frameCount }, (_, index) => {
      const seq = frameCount - 1 - index;
      return { key: `${product.id}-${site?.tile || "us"}-${seq}`, url: `${product.tile(seq, site?.tile)}?v=${bucket}` };
    });
  }, [product, site, frameCount, bucket]);
  return tiles.map((tile, index) => (
    <TileLayer
      key={tile.key}
      url={tile.url}
      opacity={index === frame ? opacity : 0}
      zIndex={index === frame ? 2 : 1}
      maxNativeZoom={product.site ? 10 : 9}
      maxZoom={13}
      keepBuffer={4}
      updateWhenIdle
      updateWhenZooming={false}
      className="sd-radar-tiles"
    />
  ));
};

const SatelliteLayer = ({ satelliteId, lon, bucket }) => {
  const layer = SATELLITES.find((item) => item.id === satelliteId);
  if (!layer || !layer.tile) return null;
  const hemisphere = hemisphereFor(lon);
  return (
    <TileLayer
      key={`${layer.id}-${hemisphere}`}
      url={`${layer.tile(hemisphere)}?v=${bucket}`}
      opacity={satelliteId === "geocolor" ? 0.9 : 0.7}
      maxNativeZoom={layer.maxNativeZoom}
      maxZoom={13}
      updateWhenIdle
      className="sd-satellite-tiles"
    />
  );
};

const WarningsLayer = ({ alerts, onSelect }) => {
  const data = useMemo(() => ({
    type: "FeatureCollection",
    features: alerts.filter((alert) => alert.geometry).map((alert) => ({ type: "Feature", geometry: alert.geometry, properties: alert })),
  }), [alerts]);
  const key = useMemo(() => data.features.map((feature) => feature.properties.id).join("|"), [data]);
  if (!data.features.length) return null;
  return (
    <GeoJSON
      key={key}
      data={data}
      style={(feature) => {
        const color = eventColor(feature.properties.event);
        return { color: color.fill, weight: 2.2, fillColor: color.fill, fillOpacity: 0.12, opacity: 0.95 };
      }}
      onEachFeature={(feature, layer) => {
        const alert = feature.properties;
        layer.bindPopup(
          `<div class="sd-popup"><div class="sd-popup-event" style="color:${eventColor(alert.event).fill}">${escapeHtml(alert.event)}</div>` +
          `<div class="sd-popup-area">${escapeHtml(alert.areaDesc || "")}</div>` +
          `<div class="sd-popup-meta">Issued ${timeAgo(alert.sent)} ago · Expires ${alert.expires ? new Date(alert.expires).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>` +
          `<button type="button" class="sd-popup-button" data-alert-id="${escapeHtml(alert.id)}">Full alert text</button></div>`,
          { className: "sd-leaflet-popup", maxWidth: 300 },
        );
        layer.on("popupopen", (event) => {
          const button = event.popup.getElement()?.querySelector(".sd-popup-button");
          if (button) button.onclick = () => { layer.closePopup(); onSelect?.(alert); };
        });
      }}
    />
  );
};

const SitesLayer = ({ sites, activeSite, onSelect }) => sites.map((site) => (
  <CircleMarker
    key={site.id}
    center={[site.lat, site.lon]}
    radius={activeSite?.id === site.id ? 6 : 4}
    pathOptions={{ color: activeSite?.id === site.id ? "#22d3ee" : "#94a3b8", weight: 1.5, fillColor: activeSite?.id === site.id ? "#22d3ee" : "#0f172a", fillOpacity: 0.9 }}
    eventHandlers={{ click: () => onSelect(site) }}
  >
    <Tooltip direction="top" offset={[0, -6]} className="sd-leaflet-tooltip">{site.tile} · {site.name}</Tooltip>
  </CircleMarker>
));

const ReportsLayer = ({ reports }) => (reports?.features || []).map((feature, index) => {
  const [lon, lat] = feature.geometry?.coordinates || [];
  if (lat === undefined) return null;
  const props = feature.properties || {};
  const color = reportColor(props.typetext);
  return (
    <CircleMarker key={`${props.valid}-${index}`} center={[lat, lon]} radius={5} pathOptions={{ color: "#020617", weight: 1, fillColor: color, fillOpacity: 0.95 }}>
      <Tooltip direction="top" offset={[0, -6]} className="sd-leaflet-tooltip">
        {props.typetext}{props.magnitude ? ` ${props.magnitude}` : ""} · {props.city}, {props.st}
      </Tooltip>
    </CircleMarker>
  );
});

const LocationMarker = ({ location }) => location ? (
  <>
    <CircleMarker center={[location.lat, location.lon]} radius={12} pathOptions={{ color: "#22d3ee", weight: 1, fillColor: "#22d3ee", fillOpacity: 0.15 }} />
    <CircleMarker center={[location.lat, location.lon]} radius={5} pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#22d3ee", fillOpacity: 1 }} />
  </>
) : null;

export const RadarMap = ({
  view, onViewChange, mapRef, product, site, frame, frameCount, radarOpacity, bucket,
  satellite, overlays, alerts, sites, reports, location, onSelectAlert, onSelectSite, compact, basemap = "dark",
}) => {
  const initialView = useRef(view);
  return (
    <MapContainer
      center={[initialView.current.lat, initialView.current.lon]}
      zoom={initialView.current.zoom}
      minZoom={3}
      maxZoom={13}
      zoomControl={false}
      attributionControl={false}
      preferCanvas
      className="sd-radar-map h-full w-full bg-[#02040c]"
      worldCopyJump
    >
      <MapBridge mapRef={mapRef} onViewChange={onViewChange} />
      <TileLayer key={`base-${basemap}`} url={(BASEMAPS[basemap] || BASEMAPS.dark).base} maxNativeZoom={(BASEMAPS[basemap] || BASEMAPS.dark).maxNativeZoom} maxZoom={13} />
      <Pane name="sd-satellite" style={{ zIndex: 250 }}>
        <SatelliteLayer satelliteId={satellite} lon={view.lon} bucket={bucket} />
      </Pane>
      <Pane name="sd-radar" style={{ zIndex: 300 }}>
        <RadarFrames product={product} site={site} frame={frame} frameCount={frameCount} opacity={radarOpacity} bucket={bucket} />
      </Pane>
      {overlays.labels && (
        <Pane name="sd-labels" style={{ zIndex: 350 }}>
          <TileLayer key={`labels-${basemap}`} url={(BASEMAPS[basemap] || BASEMAPS.dark).labels} maxNativeZoom={(BASEMAPS[basemap] || BASEMAPS.dark).maxNativeZoom} maxZoom={13} />
        </Pane>
      )}
      {overlays.warnings && <WarningsLayer alerts={alerts} onSelect={onSelectAlert} />}
      {overlays.sites && !compact && <SitesLayer sites={sites} activeSite={site} onSelect={onSelectSite} />}
      {overlays.reports && !compact && <ReportsLayer reports={reports} />}
      <LocationMarker location={location} />
    </MapContainer>
  );
};
